import { BaseUser } from "~/entities/authEntities";
import Client from "../baseClient";
import {
  OrderStatus,
  PlaceOrderRequest,
  ResponseOrderWithCartLines,
  ResponseOrderWithLines,
  ResponseOrderWithSimpleOrderLines,
} from "~/entities/orderEntities";
import { RoundingMethod, TaxAggMethod } from "~/entities/siteEntities";
import { AuthHeaders } from "~/api/endpoints/Auth";
import { PaymentSurcharge } from "~/entities/paymentEntities";

export interface OrderStatusResponse {
  groupDraftCount: number;
  groupHeldCount: number;
  status: OrderStatus;
}

export const EMPTY_CUSTOMER: BaseUser = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
};

export const EMPTY_ORDER_RESPONSE: ResponseOrderWithCartLines = {
  id: "",
  totalBeforeDiscount: 0,
  paidAmount: 0,
  lines: [],
  orderTypeCode: "",
  customer: EMPTY_CUSTOMER,
  tableNumber: null,
  orderNumber: "",
  status: OrderStatus.DRAFT,
  pendingPaymentIds: [],
  redemptionKey: null,
  promoCode: null,
  taxAggregationMethod: TaxAggMethod.PER_LINE,
  additionalCharges: [],
  discounts: [],
  finalPriceExTax: 0,
  finalPriceIncTax: 0,
  lineTotalBeforeDiscount: 0,
  pricesIncludeTax: true,
  promotionSchedulingOverride: {},
  requestedReadyTime: null,
  taxRoundingMethod: RoundingMethod.HALF_UP,
  unlockedPromotions: [],
  groupOrder: false,
  redemptionName: null,
  estimatedReadyTime: null,
};

export const OrderApi = {
  addOrder: async (
    authHeaders: AuthHeaders,
    orgId: string,
    siteId: string,
    order: PlaceOrderRequest,
    errorOnInvalidAsset: boolean | null
  ) =>
    await Client.post<ResponseOrderWithLines>(
      `${orgId}/${siteId}/orders${
        errorOnInvalidAsset ? "?errorOnInvalidAsset=true" : ""
      }`,
      {
        ...order,
      },
      {
        headers: authHeaders,
      }
    ),
  tryGetOrder: async (orgId: string, siteId: string, orderId: string) => {
    try {
      return await Client.get<ResponseOrderWithLines>(
        `${orgId}/${siteId}/orders/${orderId}`
      );
    } catch (e: any) {
      if (e.status === 404) {
        return null;
      }
      throw e;
    }
  },
  updateOrder: async (
    authHeaders: AuthHeaders,
    orgId: string,
    siteId: string,
    orderId: string,
    order: PlaceOrderRequest,
    errorOnInvalidAsset: boolean | null
  ) =>
    await Client.put<ResponseOrderWithLines>(
      `${orgId}/${siteId}/orders/${orderId}${
        errorOnInvalidAsset ? "?errorOnInvalidAsset=true" : ""
      }`,
      order,
      {
        headers: authHeaders,
      }
    ),
  clearOrder: async (
    authHeaders: AuthHeaders,
    orgId: string,
    siteId: string,
    orderId: string
  ) =>
    await Client.post(
      `${orgId}/${siteId}/orders/${orderId}/clear`,
      {},
      {
        headers: authHeaders,
      }
    ),
  getStatus: async (orgId: string, siteId: string, orderId: string) => {
    try {
      return await Client.get<OrderStatusResponse>(
        `${orgId}/${siteId}/orders/${orderId}/status`
      );
    } catch (e: any) {
      if (e.status === 404) {
        return null;
      }
      throw e;
    }
  },
  getOrderPaymentFeeInfo: async (
    orgId: string,
    siteId: string,
    orderId: string
  ) => {
    try {
      return await Client.get<PaymentSurcharge>(
        `${orgId}/${siteId}/orders/${orderId}/paymentFees`
      );
    } catch (e: any) {
      if (e.status === 404) {
        return null;
      }
      throw e;
    }
  },
  send: async (
    orgId: string,
    siteId: string,
    orderId: string,
    sendAll: boolean
  ) => {
    try {
      return await Client.post(`${orgId}/${siteId}/orders/${orderId}/send`, {
        sendAll,
      });
    } catch (e: any) {
      if (e.status === 404) {
        return null;
      }
      throw e;
    }
  },
  getOrders: async (authHeaders: AuthHeaders, orgId: string) => {
    return await Client.get<ResponseOrderWithSimpleOrderLines[]>(
      `${orgId}/orders`,
      {
        headers: authHeaders,
      }
    );
  },
  sendReceipt: async (
    authHeaders: AuthHeaders,
    orgId: string,
    siteId: string,
    orderId: string
  ) => {
    return await Client.post(
      `${orgId}/${siteId}/orders/${orderId}/sendReceipt`,
      {},
      {
        headers: authHeaders,
      }
    );
  },
};
